import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { apiManager } from "../../services/apiManager";
import { inpatientAPI, clinicAppointmentsAPI } from "../../services/api";
import { USER_TYPES } from "../../constants/userTypes";
import AdmitInpatientModal from "../modals/AdmitInpatientModal";
import PatientTypeChooserModal from "../modals/PatientTypeChooserModal";
import NewPatientWizardModal from "../modals/NewPatientWizardModal";
import "../../pages/reception/ReceptionStyles.css";
import EditInpatientModal from "../modals/EditInpatientModal";
import InpatientDetailsModal from "../modals/InpatientDetailsModal";
import InpatientVitalSignsModal from "../modals/InpatientVitalSignsModal";
import CloseInpatientModal from "../modals/CloseInpatientModal";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import { useTranslation } from "react-i18next";
import { isClinicOnFreePlan } from "../../utils/clinicPlanUtils";

const ROWS_PER_PAGE = 8;

const InpatientTab = ({
  clinicData,
  clinicDataFromHook,
  userType,
  vets,
  loadingLists,
  inpatientVisits = [],
  outpatientVisits = [],
  onDataUpdate,
  fetchAllTabData,
  onDetailsOpenChange
}) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const locale = i18n.language === "ar" ? "ar-EG" : "en-GB";
  const defaultDateString = useMemo(() => {
    try {
      return new Date()
        .toLocaleDateString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          calendar: "gregory"
        })
        .replace(/\//g, "-");
    } catch (error) {
      return "";
    }
  }, [locale]);

  const fallbackLabels = useMemo(
    () => ({
      petGuardian: t("receptionInpatients.tab.fallback.petGuardian"),
      pet: t("receptionInpatients.tab.fallback.pet"),
      vet: t("receptionInpatients.tab.fallback.vet"),
      cage: t("receptionInpatients.tab.fallback.cage"),
      reason: t("receptionInpatients.tab.fallback.reason")
    }),
    [t]
  );

  // Helper function to resolve pet profile image URL
  const getPetImageUrl = useCallback((row) => {
    // row.pet might be a string (pet name) or an object
    // Check originalData.pet first (from API), then row.pet (from MainReceptionPage)
    const pet = row.originalData?.pet || (typeof row.pet === 'object' ? row.pet : null);
    if (!pet) return "/images/no-image-pet.png";

    // Handle profile_image_path which might be a string or an object
    const profilePath = typeof pet.profile_image_path === "string"
      ? pet.profile_image_path
      : pet.profile_image_path?.url || pet.profile_image_path?.path;

    // Try multiple possible image fields
    const imageUrl =
      pet.primaryImageUrl ||
      pet.primary_image_url ||
      profilePath ||
      pet.profile_image_url ||
      pet.profileImageUrl ||
      pet.image ||
      pet.avatar;

    // If we have a URL, check if it needs the API base URL
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim()) {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      // Check if it's already a full URL (from MainReceptionPage processing)
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }
      // If it's a relative path, prepend API URL
      // Handle paths with or without leading slash
      const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
      return `${API_BASE_URL}${cleanPath}`;
    }

    return "/images/no-image-pet.png";
  }, []);

  const getPetOwnerDisplay = useCallback(
    (value) => {
      if (typeof value === "string") {
        return value.trim() || fallbackLabels.petGuardian;
      }
      const candidate =
        value?.display_name ||
        value?.full_name ||
        `${value?.first_name || ""} ${value?.last_name || ""}`.trim();
      return candidate || fallbackLabels.petGuardian;
    },
    [fallbackLabels.petGuardian]
  );

  const getPetNameDisplay = useCallback(
    (value) => {
      if (typeof value === "string") {
        return value.trim() || fallbackLabels.pet;
      }
      return (value?.name || "").trim() || fallbackLabels.pet;
    },
    [fallbackLabels.pet]
  );

  const getVetDisplay = useCallback(
    (value) => {
      if (typeof value === "string") {
        return value.trim() || fallbackLabels.vet;
      }
      const candidate =
        value?.name ||
        value?.full_name ||
        `${value?.first_name || ""} ${value?.last_name || ""}`.trim();
      return candidate || fallbackLabels.vet;
    },
    [fallbackLabels.vet]
  );

  const formatStatus = useCallback(
    (status) => {
      if (!status) return t("receptionInpatients.tab.status.unknown");
      const key = status.toLowerCase();
      return t(`receptionInpatients.tab.status.${key}`, { defaultValue: status });
    },
    [t]
  );

  const formatReason = useCallback(
    (reasonValue, vetName) => {
      let reason = reasonValue;
      if (reason && typeof reason === "object") {
        reason =
          reason.description ||
          reason.title ||
          reason.reason ||
          "";
      }
      if (typeof reason === "string" && reason.trim()) {
        return reason;
      }
      return t("receptionInpatients.tab.cards.reasonFallback", {
        vet: vetName || fallbackLabels.vet
      });
    },
    [t, fallbackLabels.vet]
  );

  const formatLengthOfStay = useCallback(
    (days) => t("receptionInpatients.tab.cards.lengthOfStay", { count: days }),
    [t]
  );

  const [localInpatientVisits, setLocalInpatientVisits] = useState(inpatientVisits);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showAdmitInpatientModal, setShowAdmitInpatientModal] = useState(false);
  const [showEditInpatientModal, setShowEditInpatientModal] = useState(false);
  const [editInpatientData, setEditInpatientData] = useState(null);
  const [page, setPage] = useState(1);
  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedInpatientId, setSelectedInpatientId] = useState(null);
  const [showVitalSignsModal, setShowVitalSignsModal] = useState(false);
  const [vitalSignsInpatientId, setVitalSignsInpatientId] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeInpatientId, setCloseInpatientId] = useState(null);

  // Patient chooser / new patient wizard flow
  const [showPatientChooser, setShowPatientChooser] = useState(false);
  const [showNewPatientWizard, setShowNewPatientWizard] = useState(false);
  const [prefillPetOwner, setPrefillPetOwner] = useState(null);
  const [prefillPet, setPrefillPet] = useState(null);
  const [search, setSearch] = useState(() => ({
    date: defaultDateString,
    pet_owner: "",
    pet: "",
    vet: "",
    status: "",
    showAllActive: true,
    showAllCheckout: false,
    showAllClosed: false,
  }));

  // Tooltip menu state
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipRow, setTooltipRow] = useState(null);

  // Help tooltip state
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const [helpTooltipPosition, setHelpTooltipPosition] = useState({ x: 0, y: 0 });

  // Search state
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [searchPagination, setSearchPagination] = useState(null);

  // Refs for Flatpickr
  const searchDateInputRef = useRef(null);
  const searchDatePickerRef = useRef(null);

  // Update local state when props change
  useEffect(() => {
    setLocalInpatientVisits(inpatientVisits);
  }, [inpatientVisits]);



  useEffect(() => {
    setSearch((prev) => ({
      ...prev,
      date: defaultDateString
    }));
  }, [defaultDateString]);

  // Search inpatient visits using API
  const searchInpatientVisits = async (searchParams = {}) => {
    try {
      setIsSearching(true);

      // Build search parameters with only non-empty values
      const params = {
        page: page,
        per_page: 5,
        clinic_id: clinicDataToUse?.clinic?.id,
      };

      // Only add parameters that have actual values
      if (searchParams.date_from) params.date_from = searchParams.date_from;
      if (searchParams.date_to) params.date_to = searchParams.date_to;
      if (searchParams.datetime_from) params.datetime_from = searchParams.datetime_from;
      if (searchParams.datetime_to) params.datetime_to = searchParams.datetime_to;
      if (searchParams.time_from) params.time_from = searchParams.time_from;
      if (searchParams.time_to) params.time_to = searchParams.time_to;
      if (searchParams.user_id) params.user_id = searchParams.user_id;
      if (searchParams.vet_id) params.vet_id = searchParams.vet_id;
      if (searchParams.pet_id) params.pet_id = searchParams.pet_id;
      if (searchParams.pet_owner_id) params.pet_owner_id = searchParams.pet_owner_id;
      if (searchParams.pet_owner_name) params.pet_owner_name = searchParams.pet_owner_name;
      if (searchParams.user_name) params.pet_owner_name = searchParams.user_name; // Maintain backward compatibility
      if (searchParams.vet_name) params.vet_name = searchParams.vet_name;
      if (searchParams.pet_name) params.pet_name = searchParams.pet_name;
      if (searchParams.clinic_name) params.clinic_name = searchParams.clinic_name;
      if (searchParams.search) params.search = searchParams.search;
      if (searchParams.canceled_by) params.canceled_by = searchParams.canceled_by;
      if (searchParams.restored_by) params.restored_by = searchParams.restored_by;
      if (searchParams.has_canceled_reason) params.has_canceled_reason = searchParams.has_canceled_reason;
      if (searchParams.today) params.today = searchParams.today;
      if (searchParams.tomorrow) params.tomorrow = searchParams.tomorrow;
      if (searchParams.this_week) params.this_week = searchParams.this_week;
      if (searchParams.this_month) params.this_month = searchParams.this_month;
      if (searchParams.upcoming) params.upcoming = searchParams.upcoming;
      if (searchParams.past) params.past = searchParams.past;
      if (searchParams.sort_by) params.sort_by = searchParams.sort_by;
      if (searchParams.sort_order) params.sort_order = searchParams.sort_order;
      if (searchParams.active_only) params.active_only = 1;

      // Handle status string - only add if it has a value (ensure single value, not array)
      if (searchParams.status && searchParams.status !== '') {
        params.status = String(searchParams.status);
      }


      // Use the clinic appointments API for inpatient visits search
      const response = await clinicAppointmentsAPI.searchInpatientVisits(params);

      if (response.data && response.data.status === "success") {
        const visits = response.data.data.inpatient_visits || [];
        const pagination = response.data.data.pagination || {};
        const total = pagination.total || visits.length;

        // Transform visits to match the expected format
        const transformedVisits = visits.map(visit => {
          // Parse visit_date to extract date and time
          let date = '';
          let time = '';

          if (visit.visit_date) {
            // visit_date format: "2025-10-12 11:04:14"
            const dateTimeParts = visit.visit_date.split(' ');
            if (dateTimeParts.length >= 2) {
              date = dateTimeParts[0]; // "2025-10-12"
              const timePart = dateTimeParts[1]; // "11:04:14"
              if (timePart) {
                time = timePart.substring(0, 5); // "11:04"
              }
            }
          }

          const petOwnerName = getPetOwnerDisplay(visit.pet_owner);
          const petName = getPetNameDisplay(visit.pet);
          const vetName = getVetDisplay(visit.vet);
          const reason = formatReason(visit.reason || visit.main_complaint || '', vetName);

          return {
            id: visit.id,
            date: date,
            time: time,
            pet_owner: petOwnerName,
            pet: petName,
            vet: vetName,
            reason,
            status: visit.status,
            type: 'inpatient',
            downpayment: visit.downpayment || 0,
            cage_no: visit.cage_no || fallbackLabels.cage,
            length_of_stay_days: visit.length_of_stay_days || 0,
            allowed_food: visit.allowed_food || "",
            allowed_fluids: visit.allowed_fluids || "",
            rate_per_day: visit.rate_per_day || 0,
            is_isolated: visit.is_isolated || false,
            originalData: visit
          };
        });

        setSearchResults(transformedVisits);
        setTotalSearchResults(total);
        setSearchPagination(pagination);

        // Update local visits for immediate UI feedback
        setLocalInpatientVisits(transformedVisits);

        return transformedVisits;
      }

      return [];
    } catch (error) {
      console.error("Error searching inpatient visits:", error);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  // Get clinic data based on user type
  const clinicDataToUse = userType === USER_TYPES.CLINIC_MODERATOR
    ? (clinicDataFromHook || clinicData)
    : clinicData;

  // Check if clinic is on free plan
  const isFreePlan = isClinicOnFreePlan(clinicDataToUse);


  // Initialize Flatpickr for search date input (match outpatient format)
  useEffect(() => {
    if (searchDateInputRef.current && !searchDatePickerRef.current) {
      import("flatpickr").then((flatpickr) => {
        searchDatePickerRef.current = flatpickr.default(searchDateInputRef.current, {
          dateFormat: "d-m-Y",
          allowInput: true,
          clickOpens: true,
          defaultDate: new Date(),
          onChange: function (selectedDates, dateStr) {
            setSearch((s) => ({ ...s, date: dateStr }));
            setPage(1);
          }
        });
      });
    }
    return () => {
      if (searchDatePickerRef.current) {
        searchDatePickerRef.current.destroy();
        searchDatePickerRef.current = null;
      }
    };
  }, []);

  // Automatically trigger search for active visits on mount
  useEffect(() => {
    if (search.showAllActive && clinicDataToUse?.clinic?.id) {
      const searchParams = {
        ...search,
        active_only: 1,
        status: undefined
      };
      searchInpatientVisits(searchParams);
    }
  }, [clinicDataToUse?.clinic?.id]);




  // Use search results if available, otherwise use local filtering
  const filteredRows = searchResults.length > 0 ? searchResults : localInpatientVisits.filter((row) => {
    // Convert row.date (YYYY-MM-DD) to DD-MM-YYYY for comparison with search.date (DD-MM-YYYY)
    const convertRowDateToDDMMYYYY = (dateStr) => {
      if (!dateStr) return '';
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert YYYY-MM-DD to DD-MM-YYYY
      }
      return dateStr;
    };

    // Date filter: validation based on showAllActive/showAllCheckout/showAllClosed flags
    // If any of these flags are true, ignore specific date and filter by status
    // Otherwise, require date match
    const isRowActive = row.status !== 'completed' && row.status !== 'canceled' && row.status !== 'checkout' && row.status !== 'closed';
    const isRowCheckout = (row.status || '').toLowerCase() === 'checkout';
    const isRowClosed = (row.status || '').toLowerCase() === 'closed' || (row.status || '').toLowerCase() === 'completed';

    let passesDateFilter = true;
    if (search.showAllActive) {
      // In "All Active" mode, only show active visits (ignore date)
      passesDateFilter = isRowActive;
    } else if (search.showAllCheckout) {
      // In "All Checkout" mode, only show checkout visits (ignore date)
      passesDateFilter = isRowCheckout;
    } else if (search.showAllClosed) {
      // In "All Closed" mode, only show closed visits (ignore date)
      passesDateFilter = isRowClosed;
    } else {
      // Normal mode: check date match
      passesDateFilter = !search.date || convertRowDateToDDMMYYYY(row.date) === search.date;
    }

    const petOwnerDisplay = getPetOwnerDisplay(row.pet_owner).toLowerCase();
    const petNameDisplay = getPetNameDisplay(row.pet).toLowerCase();
    const vetDisplay = getVetDisplay(row.vet).toLowerCase();

    const passesPetOwnerFilter = !search.pet_owner ||
      petOwnerDisplay.includes(search.pet_owner.toLowerCase());
    const passesPetFilter = !search.pet ||
      petNameDisplay.includes(search.pet.toLowerCase());
    const passesVetFilter = !search.vet ||
      vetDisplay.includes(search.vet.toLowerCase());

    const passesStatusFilter = !search.status || (row.status && row.status.toLowerCase() === search.status.toLowerCase());

    return passesDateFilter && passesPetOwnerFilter && passesPetFilter && passesVetFilter && passesStatusFilter;
  });

  // Pagination - use search results if available, otherwise use local filtering
  const totalPages = searchResults.length > 0
    ? (searchPagination?.last_page || 1)
    : Math.ceil(filteredRows.length / ROWS_PER_PAGE);

  const paginatedRows = searchResults.length > 0
    ? filteredRows // Search results are already paginated by the API
    : filteredRows.slice(
      (page - 1) * ROWS_PER_PAGE,
      page * ROWS_PER_PAGE
    );

  const handleRowLeave = () => {
    setShowHelpTooltip(false);
  };

  // Handle card click: select only (no context menu)
  const handleCardClick = (row, event) => {
    event.preventDefault();
    event.stopPropagation();
    setShowHelpTooltip(false);
    // No selection/highlight to avoid layout shifts
  };

  // Handle tooltip menu actions
  const handleTooltipAction = (action, row) => {
    setShowTooltip(false);
    setSelectedRow(row);

    switch (action) {
      case 'details':
        // Handle inpatient details view (to mirror outpatient "Details")
        // Mirror OutpatientTab: use the row's backend ID directly
        setSelectedInpatientId(row.id);
        setShowOverlay(true);
        break;
      case 'edit':
        setEditInpatientData(row);
        setShowEditInpatientModal(true);
        break;
      case 'vital_signs':
        setVitalSignsInpatientId(row.id);
        setShowVitalSignsModal(true);
        break;
      case 'discharge':
        // Handle discharge
        break;
      case 'transfer':
        // Handle transfer
        break;
      case 'close':
        // Handle close/checkout
        setCloseInpatientId(row.id);
        setShowCloseModal(true);
        break;
      default:
        break;
    }
  };

  // Notify parent when overlay opens/closes
  const handleOverlayClose = useCallback(() => {
    setShowOverlay(false);
    setSelectedInpatientId(null);
    if (onDetailsOpenChange) {
      // When overlay closes, also clear SOAP state
      onDetailsOpenChange(false, null, false, null);
    }
  }, [onDetailsOpenChange]);

  useEffect(() => {
    if (onDetailsOpenChange) {
      // Only notify about overlay state, don't pass SOAP state (undefined means don't update SOAP state)
      onDetailsOpenChange(showOverlay, showOverlay ? handleOverlayClose : null, undefined, undefined);
    }
  }, [showOverlay, onDetailsOpenChange, handleOverlayClose]);

  // Hide help tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showHelpTooltip && !event.target.closest('tr')) {
        setShowHelpTooltip(false);
      }
    };

    if (showHelpTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showHelpTooltip]);

  const hasSearchResults = searchResults.length > 0;
  // Use filtered count for display to match what the user actually sees on the screen,
  // particularly when client-side filtering is applied on top of API results.
  // If there are multiple pages, we fall back to totalSearchResults from the API.
  const displayedCount = (hasSearchResults && totalPages > 1) ? totalSearchResults : filteredRows.length;
  const countKey = hasSearchResults
    ? "receptionInpatients.tab.header.searchCount"
    : "receptionInpatients.tab.header.count";
  const countLabel = t(countKey, { count: displayedCount });

  return (
    <div className="reception-tab-content" dir={i18n.dir()} lang={i18n.language}>
      <style>{`
        .inpatient-card .action-buttons .action-btn {
          min-width: 120px !important;
          padding: 8px 16px !important;
          font-size: 0.85rem !important;
          border-radius: 6px !important;
        }
      `}</style>
      {/* Search Section - Hidden when details view is open */}
      {!showOverlay && (
        <div className="reception-search-box">
          <div className="search-input-group">
            <label className="search-label">{t("receptionInpatients.tab.search.date")}</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                ref={searchDateInputRef}
                type="text"
                className="reception-input"
                placeholder={t("receptionInpatients.tab.search.datePlaceholder")}
                value={search.date}
                disabled={search.showAllActive || search.showAllCheckout || search.showAllClosed}
                style={{ opacity: (search.showAllActive || search.showAllCheckout || search.showAllClosed) ? 0.5 : 1, cursor: (search.showAllActive || search.showAllCheckout || search.showAllClosed) ? 'not-allowed' : 'pointer' }}
                readOnly
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                type="button"
                className="quick-filter-btn"
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: search.showAllActive ? '#00d4b3' : '#f9fafb',
                  color: search.showAllActive ? '#fff' : '#6b7280',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  flex: 1,
                  transition: 'all 0.2s ease',
                  boxShadow: search.showAllActive ? '0 1px 3px rgba(0, 212, 179, 0.2)' : 'none'
                }}
                onClick={async () => {
                  const isActive = !search.showAllActive;
                  setSearch(s => ({
                    ...s,
                    showAllActive: isActive,
                    showAllCheckout: false,
                    showAllClosed: false
                  }));
                  setPage(1);

                  if (isActive) {
                    const searchParams = {
                      ...search,
                      active_only: true,
                      status: undefined
                    };
                    await searchInpatientVisits(searchParams);
                  } else {
                    setSearchResults([]);
                    setTotalSearchResults(0);
                    setSearchPagination(null);
                  }
                }}
              >
                {t("receptionInpatients.tab.search.showAllActive", { defaultValue: "Show All Active" })}
              </button>
              {userType === USER_TYPES.CLINIC_MODERATOR && (
                <>
                  <button
                    type="button"
                    className="quick-filter-btn"
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: search.showAllCheckout ? '#00d4b3' : '#f9fafb',
                      color: search.showAllCheckout ? '#fff' : '#6b7280',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      flex: 1,
                      transition: 'all 0.2s ease',
                      boxShadow: search.showAllCheckout ? '0 1px 3px rgba(0, 212, 179, 0.2)' : 'none'
                    }}
                    onClick={async () => {
                      const isActive = !search.showAllCheckout;
                      setSearch(s => ({
                        ...s,
                        showAllCheckout: isActive,
                        showAllActive: false,
                        showAllClosed: false
                      }));
                      setPage(1);

                      if (isActive) {
                        const searchParams = {
                          ...search,
                          status: 'checkout'
                        };
                        await searchInpatientVisits(searchParams);
                      } else {
                        setSearchResults([]);
                        setTotalSearchResults(0);
                        setSearchPagination(null);
                      }
                    }}
                  >
                    {t("receptionInpatients.tab.search.showAllCheckout", { defaultValue: "Show All Checkout" })}
                  </button>
                  <button
                    type="button"
                    className="quick-filter-btn"
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: search.showAllClosed ? '#00d4b3' : '#f9fafb',
                      color: search.showAllClosed ? '#fff' : '#6b7280',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      flex: 1,
                      transition: 'all 0.2s ease',
                      boxShadow: search.showAllClosed ? '0 1px 3px rgba(0, 212, 179, 0.2)' : 'none'
                    }}
                    onClick={async () => {
                      const isActive = !search.showAllClosed;
                      setSearch(s => ({
                        ...s,
                        showAllClosed: isActive,
                        showAllActive: false,
                        showAllCheckout: false
                      }));
                      setPage(1);

                      if (isActive) {
                        const searchParams = {
                          ...search,
                          status: 'completed'
                        };
                        await searchInpatientVisits(searchParams);
                      } else {
                        setSearchResults([]);
                        setTotalSearchResults(0);
                        setSearchPagination(null);
                      }
                    }}
                  >
                    {t("receptionInpatients.tab.search.showAllClosed", { defaultValue: "Show All Closed" })}
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="search-input-group">
            <label className="search-label">{t("receptionInpatients.tab.search.petGuardian")}</label>
            <input
              type="text"
              placeholder={t("receptionInpatients.tab.search.petGuardianPlaceholder")}
              className="reception-input"
              value={search.pet_owner}
              onChange={(e) => {
                setSearch((s) => ({ ...s, pet_owner: e.target.value }));
                setPage(1);
              }}
            />
          </div>
          <div className="search-input-group">
            <label className="search-label">{t("receptionInpatients.tab.search.petName")}</label>
            <input
              type="text"
              placeholder={t("receptionInpatients.tab.search.petNamePlaceholder")}
              className="reception-input"
              value={search.pet}
              onChange={(e) => {
                setSearch((s) => ({ ...s, pet: e.target.value }));
                setPage(1);
              }}
            />
          </div>
          <div className="search-input-group">
            <label className="search-label">{t("receptionInpatients.tab.search.vet")}</label>
            <input
              type="text"
              placeholder={t("receptionInpatients.tab.search.vetPlaceholder")}
              className="reception-input"
              value={search.vet}
              onChange={(e) => {
                setSearch((s) => ({ ...s, vet: e.target.value }));
                setPage(1);
              }}
            />
          </div>
          <div className="search-input-group">
            <label className="search-label">{t("receptionInpatients.tab.search.status")}</label>
            <select
              className="reception-input"
              value={search.status || ""}
              onChange={(e) => {
                const value = e.target.value;
                setSearch((s) => ({ ...s, status: value }));
                setPage(1);
              }}
            >
              <option value="">{t("receptionInpatients.tab.search.statusOptions.all")}</option>
              <option value="pending">{formatStatus("pending")}</option>
              <option value="confirmed">{formatStatus("confirmed")}</option>
              <option value="completed">{formatStatus("completed")}</option>
              <option value="canceled">{formatStatus("canceled")}</option>
              <option value="checkout">{formatStatus("checkout")}</option>
            </select>
          </div>
          <div className="search-input-group">
            <label className="search-label">&nbsp;</label>
            <input
              type="button"
              className="reception-search-btn reception-input"
              value={isSearching ? t("receptionInpatients.tab.search.searching") : t("receptionInpatients.tab.search.button")}
              disabled={isSearching}
              onClick={async (e) => {
                e.preventDefault();
                setPage(1);

                // Convert DD-MM-YYYY to YYYY-MM-DD for API
                const convertDateFormat = (dateStr) => {
                  if (!dateStr) return '';
                  const parts = dateStr.split('-');
                  if (parts.length === 3) {
                    return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert DD-MM-YYYY to YYYY-MM-DD
                  }
                  return dateStr;
                };

                // Map basic search fields to API parameters
                const searchParams = {
                  ...search,
                  // Override with specific datetime parameters for date search
                  date_from: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? convertDateFormat(search.date) : '',
                  date_to: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? convertDateFormat(search.date) : '',
                  pet_owner_name: search.pet_owner,
                  vet_name: search.vet || search.vet_name,
                  pet_name: search.pet || search.pet_name,
                  search: search.pet_owner || search.pet || search.vet || search.date || search.search,
                  // Ensure status is sent as single value, not array
                  // For closed, we search for 'completed' since closing sets status to 'completed'
                  status: search.showAllCheckout ? 'checkout' : search.showAllClosed ? 'completed' : search.status || undefined,
                  active_only: search.showAllActive ? 1 : undefined
                };

                await searchInpatientVisits(searchParams);
              }}
            />
          </div>
        </div>
      )}

      {/* Cards Section OR Details View */}
      {showOverlay ? (
        <InpatientDetailsModal
          isOpen={showOverlay}
          inpatientId={selectedInpatientId}
          onClose={handleOverlayClose}
          workingHours={clinicDataToUse?.clinic?.working_hours || []}
          fetchAllTabData={fetchAllTabData}
        />
      ) : (
        <div className="reception-cards-wrapper">
          <div className="cards-header">
            <div className="cards-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="cards-title-left">
                {t("receptionInpatients.tab.header.title")}
                <span className="cards-count">
                  {countLabel}
                  {hasSearchResults && (
                    <span style={{ color: '#059669', marginInlineStart: '8px' }}>
                      🔍 {t("receptionInpatients.tab.header.searchTag")}
                    </span>
                  )}
                </span>
              </div>
              <div className="cards-title-right">
                {hasSearchResults && (
                  <button
                    className="reception-action-btn"
                    style={{ marginRight: '8px', backgroundColor: '#6b7280' }}
                    onClick={() => {
                      // Clear search results
                      setSearchResults([]);
                      setTotalSearchResults(0);
                      setSearchPagination(null);
                      setPage(1);

                      // Reset all search filters
                      setSearch({
                        date: defaultDateString,
                        pet_owner: "",
                        pet: "",
                        vet: "",
                        status: "",
                        showAllActive: true,
                        showAllCheckout: false,
                        showAllClosed: false,
                      });

                      // Reset date picker to current date if it exists
                      if (searchDatePickerRef.current) {
                        searchDatePickerRef.current.setDate(new Date());
                      }
                    }}
                  >
                    {t("receptionInpatients.tab.search.clear")}
                  </button>
                )}
                <button
                  className="reception-action-btn"
                  onClick={() => {
                    if (!isFreePlan) {
                      setShowPatientChooser(true);
                    }
                  }}
                  disabled={isFreePlan}
                  style={{
                    position: 'relative',
                    opacity: isFreePlan ? 0.6 : 1,
                    cursor: isFreePlan ? 'not-allowed' : 'pointer'
                  }}
                  title={isFreePlan ? "Feature Locked, Upgrade to Unlock" : undefined}
                >
                  {t("receptionInpatients.tab.buttons.new")}
                  {isFreePlan && (
                    <span
                      className="feature-lock-icon-small"
                      style={{
                        marginLeft: '8px',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                      title="Feature Locked, Upgrade to Unlock"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {loadingLists || isSearching ? (
            <div className="cards-loading-state">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <span>
                  {isSearching
                    ? t("receptionInpatients.tab.loading.searching")
                    : t("receptionInpatients.tab.loading.loading")}
                </span>
              </div>
            </div>
          ) : paginatedRows.length === 0 ? (
            <div className="cards-empty-state">
              <div className="empty-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
                <h4>{t("receptionInpatients.tab.empty.title")}</h4>
                <p>{t("receptionInpatients.tab.empty.description")}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="inpatient-cards-grid">
                {paginatedRows.map((row, idx) => {
                  const petOwnerDisplay = getPetOwnerDisplay(row.pet_owner);
                  const petNameDisplay = getPetNameDisplay(row.pet);
                  const vetDisplay = getVetDisplay(row.vet);
                  const cageDisplay =
                    row.cage_no && row.cage_no !== "N/A" ? row.cage_no : fallbackLabels.cage;
                  const reasonDisplay = formatReason(row.reason, vetDisplay);

                  return (
                    <div
                      key={idx + (page - 1) * ROWS_PER_PAGE}
                      data-card-id={row.id}
                      className={`inpatient-card card-hoverable`}
                      onMouseLeave={handleRowLeave}
                      onClick={(e) => {
                        handleCardClick(row, e);
                      }}
                      style={{
                        cursor: "pointer",
                        transition: "all 0.3s ease"
                      }}
                    >
                      {/* Modern Card Design */}
                      <div className="modern-card">
                        {/* Top Section - Pet & Status */}
                        <div className="card-top-section">
                          <div className="pet-section">
                            <div className="pet-avatar">
                              <img
                                src={getPetImageUrl(row)}
                                alt={petNameDisplay}
                                className="avatar-image"
                                onError={(e) => {
                                  e.target.src = "/images/no-image-pet.png";
                                }}
                              />
                            </div>
                            <div className="pet-info">
                              <h3 className="pet-name">{petNameDisplay}</h3>
                              <p className="pet-owner">
                                {t("receptionInpatients.tab.cards.owner", {
                                  owner: petOwnerDisplay
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="status-section">
                            <span className={`status-badge ${row.status || 'admitted'}`}>
                              {formatStatus(row.status || 'admitted')}
                            </span>
                          </div>
                        </div>

                        {/* Middle Section - Visit Details */}
                        <div className="card-middle-section">
                          <div className="visit-info">
                            <div className="info-row">
                              <div className="info-item">
                                <div className="info-icon">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                  </svg>
                                </div>
                                <span className="info-text">{row.date}</span>
                              </div>

                              {typeof row.length_of_stay_days === 'number' && (
                                <div className="info-item">
                                  <div className="info-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M3 3h18v18H3z" />
                                      <path d="M3 9h18" />
                                    </svg>
                                  </div>
                                  <span className="info-text">
                                    {formatLengthOfStay(Number(row.billable_stay_days || row.length_of_stay_days || 0))}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="info-row">
                              <div className="info-item">
                                <div className="info-icon">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="8.5" cy="7" r="4" />
                                  </svg>
                                </div>
                                <span className="info-text vet-name">
                                  {vetDisplay}
                                </span>
                              </div>
                              <div className="info-item">
                                <div className="info-icon">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14" />
                                    <path d="M2 20h20" />
                                    <path d="M14 12v.01" />
                                  </svg>
                                </div>
                                <span className="info-text room">
                                  {cageDisplay}
                                </span>
                              </div>

                            </div>
                          </div>
                        </div>

                        {/* Visit Reason */}
                        <div className="card-reason-section">
                          <p className="visit-reason">
                            {t("receptionInpatients.tab.cards.reasonLabel")}{" "}
                            {reasonDisplay}
                          </p>
                        </div>
                        {/* Bottom Section - Actions */}
                        <div className="card-bottom-section">
                          <div className="action-buttons">
                            {userType !== USER_TYPES.CLINIC_MODERATOR && (
                              <button
                                className="action-btn primary"
                                style={{ minWidth: '120px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTooltipAction('details', row);
                                }}
                              >
                                <i className="fas fa-eye"></i>
                                {t("receptionInpatients.tab.cards.buttons.details")}
                              </button>
                            )}
                            {((row.status || '').toLowerCase() !== 'checkout' && (row.status || '').toLowerCase() !== 'discharged') && (
                              <button
                                className="action-btn secondary"
                                style={{ minWidth: '120px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTooltipAction('edit', row);
                                }}
                                title={t("receptionInpatients.tab.cards.buttons.editTooltip")}
                              >
                                <i className="fas fa-edit"></i>
                                {t("receptionInpatients.tab.cards.buttons.edit")}
                              </button>
                            )}
                            {(row.status || '').toLowerCase() === 'checkout' && userType === USER_TYPES.CLINIC_MODERATOR && (
                              <button
                                className="action-btn close"
                                style={{ minWidth: '120px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTooltipAction('close', row);
                                }}
                                title={t("receptionInpatients.tab.cards.buttons.closeTooltip")}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                {t("receptionInpatients.tab.cards.buttons.close")}
                              </button>
                            )}
                            {/* <button
                          className="action-btn tertiary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTooltipAction('discharge', row);
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3,6 5,6 21,6" />
                            <path d="M19,6v14a2,2 0 0,1-2,2H7a2,2 0 0,1-2-2V6m3,0V4a2,2 0 0,1,2-2h4a2,2 0 0,1,2,2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                          Discharge
                        </button> */}
                            {/* Transfer button removed as requested */}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="reception-pagination">
                  <button
                    className="reception-action-btn"
                    onClick={async () => {
                      const newPage = Math.max(1, page - 1);
                      setPage(newPage);

                      // If we have search results, trigger a new search with the new page
                      if (searchResults.length > 0) {
                        // Convert DD-MM-YYYY to YYYY-MM-DD for API
                        const convertDateFormat = (dateStr) => {
                          if (!dateStr) return '';
                          const parts = dateStr.split('-');
                          if (parts.length === 3) {
                            return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert DD-MM-YYYY to YYYY-MM-DD
                          }
                          return dateStr;
                        };

                        const searchParams = {
                          ...search,
                          datetime_from: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? `${convertDateFormat(search.date)} 00:00:00` : '',
                          datetime_to: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? `${convertDateFormat(search.date)} 23:59:59` : '',
                          user_name: search.pet_owner || search.user_name,
                          vet_name: search.vet || search.vet_name,
                          pet_name: search.pet || search.pet_name,
                          search: search.pet_owner || search.pet || search.vet || search.date || search.search,
                          status: search.showAllCheckout ? 'checkout' : search.showAllClosed ? 'closed' : search.status || undefined,
                          active_only: search.showAllActive ? 1 : undefined,
                          page: newPage
                        };
                        await searchInpatientVisits(searchParams);
                      }
                    }}
                    disabled={page === 1 || isSearching}
                  >
                    {t("receptionInpatients.tab.pagination.previous")}
                  </button>
                  <span>
                    {t("receptionInpatients.tab.pagination.pageCount", { current: page, total: totalPages })}
                  </span>
                  <button
                    className="reception-action-btn"
                    onClick={async () => {
                      const newPage = Math.min(totalPages, page + 1);
                      setPage(newPage);

                      // If we have search results, trigger a new search with the new page
                      if (searchResults.length > 0) {
                        // Convert DD-MM-YYYY to YYYY-MM-DD for API
                        const convertDateFormat = (dateStr) => {
                          if (!dateStr) return '';
                          const parts = dateStr.split('-');
                          if (parts.length === 3) {
                            return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert DD-MM-YYYY to YYYY-MM-DD
                          }
                          return dateStr;
                        };

                        const searchParams = {
                          ...search,
                          datetime_from: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? `${convertDateFormat(search.date)} 00:00:00` : '',
                          datetime_to: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? `${convertDateFormat(search.date)} 23:59:59` : '',
                          user_name: search.pet_owner || search.user_name,
                          vet_name: search.vet || search.vet_name,
                          pet_name: search.pet || search.pet_name,
                          search: search.pet_owner || search.pet || search.vet || search.date || search.search,
                          status: search.showAllCheckout ? 'checkout' : search.showAllClosed ? 'closed' : search.status || undefined,
                          active_only: search.showAllActive ? 1 : undefined,
                          page: newPage
                        };
                        await searchInpatientVisits(searchParams);
                      }
                    }}
                    disabled={page === totalPages || isSearching}
                  >
                    {t("receptionInpatients.tab.pagination.next")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Tooltip menu removed */}

      {/* Help Tooltip */}
      {showHelpTooltip && (
        <div
          className="help-tooltip"
          style={{
            position: 'fixed',
            top: helpTooltipPosition.y - 40,
            left: helpTooltipPosition.x - 100,
            zIndex: 1001,
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            pointerEvents: 'none',
            animation: 'fadeInUp 0.2s ease-out',
            maxWidth: '200px',
            textAlign: 'center'
          }}
        >
          💡 {t("receptionInpatients.tab.helpTooltip")}
        </div>
      )}
      {/* Admit New Inpatient Modal */}
      {/* Patient Type Chooser Modal */}
      <PatientTypeChooserModal
        isOpen={showPatientChooser}
        onClose={() => setShowPatientChooser(false)}
        visitType="inpatient"
        onExistingPatient={() => {
          setPrefillPetOwner(null);
          setPrefillPet(null);
          setShowAdmitInpatientModal(true);
        }}
        onNewPatient={() => {
          setShowNewPatientWizard(true);
        }}
      />

      {/* New Patient Wizard Modal */}
      <NewPatientWizardModal
        isOpen={showNewPatientWizard}
        onClose={() => setShowNewPatientWizard(false)}
        visitType="inpatient"
        onComplete={(user, pet) => {
          setPrefillPetOwner(user);
          setPrefillPet(pet);
          setShowAdmitInpatientModal(true);
        }}
      />

      {showAdmitInpatientModal && (
        <AdmitInpatientModal
          isOpen={showAdmitInpatientModal}
          onClose={() => {
            setShowAdmitInpatientModal(false);
            setPrefillPetOwner(null);
            setPrefillPet(null);
          }}
          clinic={clinicDataToUse?.clinic}
          vets={vets || []}
          inpatientVisits={inpatientVisits}
          outpatientVisits={outpatientVisits}
          prefillPetOwner={prefillPetOwner}
          prefillPet={prefillPet}
          onVisitCreated={async () => {
            // Always refresh from backend to get the real inpatient_id
            if (typeof fetchAllTabData === 'function') {
              await fetchAllTabData(true);
            }
          }}
        />
      )}

      {/* Edit Inpatient Modal */}
      {showEditInpatientModal && (
        <EditInpatientModal
          isOpen={showEditInpatientModal}
          onClose={() => {
            setShowEditInpatientModal(false);
            setEditInpatientData(null);
          }}
          vets={vets || []}
          workingHours={clinicDataToUse?.clinic?.working_hours || []}
          editData={editInpatientData}
          onSave={(updatedRow) => {
            const updated = localInpatientVisits.map((r) =>
              r.id === updatedRow.id ? { ...r, ...updatedRow } : r
            );
            setLocalInpatientVisits(updated);
            setShowEditInpatientModal(false);
            setEditInpatientData(null);
            fetchAllTabData(true);
          }}
        />
      )}

      {/* Checklist handled inside AdmitInpatientModal */}

      {/* Vital Signs Modal */}
      {showVitalSignsModal && (
        <InpatientVitalSignsModal
          isOpen={showVitalSignsModal}
          onClose={() => {
            setShowVitalSignsModal(false);
            setVitalSignsInpatientId(null);
          }}
          inpatientId={vitalSignsInpatientId}
          onSave={(savedData) => {
            // Optionally refresh data or show success message
            // You can call fetchAllTabData if needed to refresh the list
            if (typeof fetchAllTabData === 'function') {
              fetchAllTabData(true);
            }
          }}
        />
      )}

      {/* Close Inpatient Modal */}
      {showCloseModal && (
        <CloseInpatientModal
          isOpen={showCloseModal}
          onClose={() => {
            setShowCloseModal(false);
            setCloseInpatientId(null);
          }}
          inpatientId={closeInpatientId}
          onSuccess={() => {
            // Refresh data after successful close
            if (typeof fetchAllTabData === 'function') {
              fetchAllTabData(true);
            }
          }}
          paymentAmount={selectedRow.remaining_amount}
          totalAmount={selectedRow.total_amount}
          downpayment={selectedRow.downpayment}
        />
      )}
    </div>
  );
};

export default InpatientTab;
